/**
 * core/api/client.ts
 *
 * GraphQL transport layer for Chanelle OS.
 *
 * ALL monday.com data queries/mutations are routed through the Cloudflare
 * Worker proxy (/api/monday) so that MONDAY_API_TOKEN never touches the
 * browser bundle.
 *
 * The monday SDK is kept ONLY for:
 *   • monday.listen('context', ...)   — app context / frame lifecycle
 *   • monday.storage.instance.*       — app-scoped key-value store
 *   • monday.execute(...)             — open item card, confirm dialog, etc.
 */

import mondaySdk from 'monday-sdk-js';

export const monday = mondaySdk();

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/**
 * In production this should be the deployed Worker URL, e.g.
 * https://chanelle-os-worker.your-account.workers.dev
 *
 * In local development the Vite dev server proxies /api → localhost:8787,
 * so we can safely default to '' (relative path) for both environments.
 */
const WORKER_BASE =
  (process.env.VITE_WORKER_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const MONDAY_ENDPOINT = `${WORKER_BASE}/api/monday`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GqlResponse<T = unknown> {
  data: T;
  errors?: Array<{ message: string; locations?: unknown; path?: unknown }>;
  account_id?: number;
}

export class GqlError extends Error {
  public readonly errors: GqlResponse['errors'];
  constructor(message: string, errors: GqlResponse['errors']) {
    super(message);
    this.name = 'GqlError';
    this.errors = errors;
  }
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

/**
 * Execute a GraphQL query or mutation via the Cloudflare Worker proxy.
 *
 * @param query     - GraphQL document string
 * @param variables - Optional variables map
 * @returns         - Typed response data (the `data` key from the GQL response)
 * @throws GqlError - When the API returns GraphQL error objects
 * @throws Error    - On network / Worker-level failures
 */
export async function gqlClient<T = unknown>(
  query: string,
  variables?: Record<string, unknown>,
): Promise<T> {
  let httpResponse: Response;

  try {
    httpResponse = await fetch(MONDAY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables: variables ?? {} }),
    });
  } catch (networkErr) {
    throw new Error(
      `Network error reaching Chanelle OS worker: ${(networkErr as Error).message}. ` +
        `Is the worker running? (wrangler dev on port 8787)`,
    );
  }

  if (!httpResponse.ok) {
    const text = await httpResponse.text().catch(() => '');
    throw new Error(
      `Worker responded with HTTP ${httpResponse.status}: ${text.slice(0, 200)}`,
    );
  }

  const response = (await httpResponse.json()) as GqlResponse<T>;

  if (response.errors && response.errors.length > 0) {
    const message = response.errors.map((e) => e.message).join('; ');
    throw new GqlError(message, response.errors);
  }

  return response.data;
}

// ---------------------------------------------------------------------------
// Worker health check
// ---------------------------------------------------------------------------

export async function checkWorkerHealth(): Promise<{
  ok: boolean;
  tokenConfigured: boolean;
  message?: string;
}> {
  try {
    const res = await fetch(`${WORKER_BASE}/api/health`);
    if (!res.ok) return { ok: false, tokenConfigured: false, message: `HTTP ${res.status}` };
    const data = (await res.json()) as { status: string; tokenConfigured: boolean };
    return { ok: data.status === 'ok', tokenConfigured: data.tokenConfigured };
  } catch (err) {
    return { ok: false, tokenConfigured: false, message: (err as Error).message };
  }
}

// ---------------------------------------------------------------------------
// Storage helpers (monday SDK — NOT routed via Worker)
// ---------------------------------------------------------------------------

/**
 * Read a value from monday Storage (app-scoped, per user).
 * Returns `null` if the key is not found.
 */
export async function storageGet<T = string>(key: string): Promise<T | null> {
  try {
    const result = await monday.storage.instance.getItem(key);
    const raw = (result as { data?: { value?: string } })?.data?.value;
    if (raw === undefined || raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return raw as unknown as T;
    }
  } catch {
    return null;
  }
}

/**
 * Write a value to monday Storage. Serialises objects to JSON automatically.
 */
export async function storageSet(key: string, value: unknown): Promise<void> {
  const serialised = typeof value === 'string' ? value : JSON.stringify(value);
  await monday.storage.instance.setItem(key, serialised);
}

/**
 * Delete a key from monday Storage.
 */
export async function storageDelete(key: string): Promise<void> {
  await monday.storage.instance.deleteItem(key);
}

// Convenience typed wrappers
export async function storageGetJSON<T>(key: string): Promise<T | null> {
  return storageGet<T>(key);
}
export async function storageSetJSON<T>(key: string, value: T): Promise<void> {
  return storageSet(key, value);
}

// ---------------------------------------------------------------------------
// Context listener (monday SDK)
// ---------------------------------------------------------------------------

export function listenToContext(
  callback: (ctx: Record<string, unknown>) => void,
): void {
  monday.listen('context', (res: { data: Record<string, unknown> }) => {
    callback(res.data);
  });
}

// ---------------------------------------------------------------------------
// Column value parsers (read)
// ---------------------------------------------------------------------------

export function parseColumnValue(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed === 'object' && parsed !== null) {
      const obj = parsed as Record<string, unknown>;
      return String(obj.text ?? obj.label ?? obj.value ?? '');
    }
    return String(parsed);
  } catch {
    return String(raw);
  }
}

export function parseColumnJSON<T>(raw: string | null | undefined): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function parseCheckbox(raw: string | null | undefined): boolean {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { checked?: string | boolean };
    return parsed?.checked === true || parsed?.checked === 'true';
  } catch {
    return false;
  }
}

export function parseNumber(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// Column value serialisers (write / mutations)
// ---------------------------------------------------------------------------

export function serializeStatusValue(label: string): string {
  return JSON.stringify({ label });
}

export function serializeDateValue(date: string): string {
  // date must be ISO 8601, e.g. "2024-12-31"
  return JSON.stringify({ date });
}

export function serializePeopleValue(
  ids: Array<{ id: number; kind: 'person' | 'team' }>,
): string {
  return JSON.stringify({ personsAndTeams: ids });
}

export function serializeDropdownValue(labels: string[]): string {
  return JSON.stringify({ labels });
}

export function serializeCheckboxValue(checked: boolean): string {
  return JSON.stringify({ checked: checked ? 'true' : '' });
}

export function serializeNumberValue(value: number): string {
  return String(value);
}

export function serializeLinkValue(url: string, text?: string): string {
  return JSON.stringify({ url, text: text ?? url });
}

export function serializeBoardRelationValue(itemIds: string[]): string {
  return JSON.stringify({ item_ids: itemIds.map(Number) });
}
